import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from '@thd-olt-component-react/router';
import {
  useHelmet, TitleTag, MetaTag, ScriptTag, LinkTag
} from '@thd-nucleus/thd-helmet';
import {
  arrayOf as arrayType,
  customType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  json,
  useDataModel,
  extend
} from '@thd-nucleus/data-sources';
import { useStoreId, useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';

import {
  getTitleTag,
  getnValue,
  removeSpecialCharacters,
  parseCrumbs,
  processBreadcrumbs,
  getDescription,
  getAllowedNoFollow,
} from './metadata-helpers';
import { getBreadcrumbStructuredData } from './structured-data/getBreadcrumbStructuredData';
import { getBrowseSearchStructuredData, getEmtStructuredData } from './structured-data/getBrowseSearchStructuredData';
import { OPEN_GRAPH_FACEBOOK_ADMIN } from '../constants';

const getSchemaValues = (data) => {
  if (!data) return {};

  try {
    const schema = JSON.parse(data);

    return { schema };
  } catch (error) {
    return {};
  }
};

const getMetadataValues = (emtResponse) => {
  const emtContent = emtResponse?.emtContent?.content?.metadata || {};
  const { schema = '' } = getSchemaValues(emtContent?.schema);

  return {
    emtTitle: emtContent?.title,
    emtMetaDescription: emtContent?.metaDescription,
    emtOgTitle: emtContent?.ogTitle,
    emtOgDescription: emtContent?.ogDescription,
    emtOgType: emtContent?.ogType,
    emtOgImage: emtContent?.ogImage,
    emtSchema: schema
  };
};

export const getCanonicalUrlForPriceRange = (emtResponse, location) => {
  const priceRangeRefinementOptions = emtResponse?.emtContent?.content?.priceRangeRefinement;
  if (!priceRangeRefinementOptions) return null;

  const { pathname = '', search = '' } = location;
  const searchParams = new URLSearchParams(search);

  const lowerBound = searchParams.get('lowerBound') || searchParams.get('lowerbound') || 0;
  const upperBound = searchParams.get('upperBound') || searchParams.get('upperbound') || 0;
  const priceKey = `lb-${lowerBound}-ub-${upperBound}`;

  const canonicalUrl = priceRangeRefinementOptions[priceKey];
  if (!canonicalUrl) return null;

  const parsedUrl = new URL(canonicalUrl);
  const matches = parsedUrl.pathname === pathname && parsedUrl.search === search;

  return matches ? canonicalUrl : null;
};

const getRobotsTag = (
  pathWithQueryParams,
  canonicalUrl,
  priceRangeExceptionCanonical,
  searchPageIndexingExceptionCanonical
) => {
  const isNoFollow = priceRangeExceptionCanonical || searchPageIndexingExceptionCanonical
    ? false
    : getAllowedNoFollow(pathWithQueryParams, canonicalUrl);

  return new MetaTag({
    name: isNoFollow ? 'ROBOTS' : 'robots',
    content: isNoFollow
      ? 'NOINDEX, NOFOLLOW'
      : 'max-image-preview:large',
    id: 'robots',
  });
};

const getScriptTag = (isContentPageOnly, processedBreadcrumbs, title, canonical, emtData, emtSchema, products) => {
  if (isContentPageOnly) {
    const breadcrumbStructuredData = getBreadcrumbStructuredData({
      canonical,
      breadcrumbs: processedBreadcrumbs,
      title
    });

    return new ScriptTag({ content: breadcrumbStructuredData, id: 'breadcrumbStructuredData' });
  }
  const faqs = emtData?.emtContent?.content?.questionAndAnswer;
  const schema = emtSchema
    ? getEmtStructuredData({
      faqs,
      schema: emtSchema,
      breadcrumbs: processedBreadcrumbs
    })
    : getBrowseSearchStructuredData({
      breadcrumbs: processedBreadcrumbs,
      canonical,
      faqs,
      skus: products,
      title
    });

  return new ScriptTag({ content: schema, id: 'browseSearchStructuredData' });

};

const getType = (isTableView, isContentPageOnly, isBrandPage, isHybrid) => {
  let type = 'PLP';
  if (isTableView) {
    type = 'PLP Table View';
  }
  if (isContentPageOnly) {
    type = isBrandPage ? 'Category brand' : 'Category';
  } else if (isHybrid) {
    type = isBrandPage ? 'cat PLP brand' : 'cat PLP';
    if (isTableView && type === 'cat PLP') {
      type = 'cat PLP Table View';
    }
  } else if (isBrandPage) {
    type = 'PLP brand';
  }

  return type;
};

const getBrowseSearchTitle = ({
  isContentPageOnly,
  parsedTemplates,
  pageType,
  categories,
  refinements,
  searchReportKeyword,
  pathWithQueryParams
}) => {
  if (isContentPageOnly) {
    return parsedTemplates?.options?.metaData?.title;
  }

  return getTitleTag({
    pageType, categories, refinements, searchVal: searchReportKeyword, pathWithQueryParams
  });
};

const getBrowseSearchDescription = ({
  pageType, categories, refinements, searchReportKeyword, isContentPageOnly, parsedTemplates
}) => {
  if (isContentPageOnly && !(categories[0] === 'Appliances' && pageType === 'b')) {
    return parsedTemplates?.options?.metaData?.description;
  }
  return getDescription(pageType, categories, refinements, searchReportKeyword);
};

const browseHandler = ({ data, props = {} }) => {
  if (!data?.searchModel) return null;
  const tags = [];
  const { pathWithQueryParams, isTableView } = props;
  const { emtData, location, isSearchPageIndexingEnabled } = data;
  const {
    searchReport,
    metadata,
    taxonomy,
    templates,
    products
  } = data?.searchModel;

  let parsedTemplates;

  try {
    parsedTemplates = JSON.parse(templates?.[0]);
  } catch {
    parsedTemplates = templates?.[0];
  }

  const {
    emtTitle,
    emtMetaDescription,
    emtOgTitle,
    emtOgDescription,
    emtOgType,
    emtOgImage,
    emtSchema
  } = getMetadataValues(emtData);

  let searchPageIndexingExceptionCanonical;
  const searchPageCanonicalPath = emtData?.emtContent?.content?.searchPageIndexing?.canonicalUrl;

  searchPageIndexingExceptionCanonical = isSearchPageIndexingEnabled
    ? searchPageCanonicalPath
    : null;

  const priceRangeExceptionCanonical = getCanonicalUrlForPriceRange(emtData, location);

  const nValue = getnValue(metadata?.canonicalUrl);
  const { keyword: searchReportKeyword } = searchReport || {};
  const defaultCanonical = `https://www.homedepot.com${metadata?.canonicalUrl}`;
  const canonical = priceRangeExceptionCanonical
    || searchPageIndexingExceptionCanonical
    || defaultCanonical;
  const pageType = pathWithQueryParams.indexOf('/b/') > -1 ? 'b' : 's';
  const processedBreadcrumbs = parseCrumbs([...(taxonomy?.breadCrumbs || [])], searchReportKeyword);
  const isContentPageOnly = parsedTemplates?.options?.content_type === 'categorypage';
  const [categories, refinements] = processBreadcrumbs(taxonomy?.breadCrumbs);
  const isHybrid = metadata?.hasPLPBanner;
  const isBrandPage = taxonomy?.breadCrumbs?.some((crumb) => crumb.dimensionName === 'Brand');
  const type = getType(isTableView, isContentPageOnly, isBrandPage, isHybrid);

  const description = getBrowseSearchDescription({
    pageType, categories, refinements, searchReportKeyword, isContentPageOnly, parsedTemplates
  });

  const title = getBrowseSearchTitle({
    isContentPageOnly, parsedTemplates, pageType, categories, refinements, searchReportKeyword, pathWithQueryParams
  });

  const finalTitle = emtTitle || title;
  const finalMetaDescription = emtMetaDescription || description;
  const finalOgTitle = emtOgTitle || title;
  const finalOgDescription = emtOgDescription || description;
  const finalOgType = emtOgType || type;

  tags.push(new TitleTag(finalTitle));
  tags.push(new LinkTag({ rel: 'canonical', href: canonical, id: 'canonical' }));
  tags.push(new MetaTag({ name: 'description', content: finalMetaDescription, id: 'description' }));
  tags.push(new MetaTag({ property: 'og:title', content: finalOgTitle, id: 'ogTitle' }));
  tags.push(new MetaTag({ property: 'og:url', content: canonical, id: 'ogUrl' }));
  tags.push(new MetaTag({ property: 'og:type', content: finalOgType, id: 'ogType' }));
  if (emtOgImage) tags.push(new MetaTag({ property: 'og:image', content: emtOgImage, id: 'ogImage' }));
  tags.push(new MetaTag({ property: 'og:description', content: finalOgDescription, id: 'ogDescription' }));
  tags.push(new MetaTag({ property: 'og:site_name', content: 'The Home Depot', id: 'ogSiteName' }));
  tags.push(new MetaTag({ property: 'fb:admins', content: OPEN_GRAPH_FACEBOOK_ADMIN, id: 'fbAdmins' }));

  if (processedBreadcrumbs?.length > 0) {
    const firstLabel = processedBreadcrumbs[0].label;
    const formattedLabel = removeSpecialCharacters(firstLabel).replace(/\s+/g, '-');
    const androidHref = `android-app://com.thehomedepot/homedepot/categoryid/${nValue}/${formattedLabel}`;

    tags.push(new LinkTag({
      rel: 'alternate',
      media: 'only screen and (max-width: 640px)',
      href: androidHref,
      id: 'alternate'
    }));
  }

  const robotsTag = getRobotsTag(
    pathWithQueryParams, metadata?.canonicalUrl, priceRangeExceptionCanonical, searchPageIndexingExceptionCanonical
  );
  tags.push(robotsTag);

  const scriptTag = getScriptTag(
    isContentPageOnly, processedBreadcrumbs, finalTitle, canonical, emtData, emtSchema, products
  );
  tags.push(scriptTag);

  return tags;
};

const BrowseSearchMetadata = (props) => {
  const {
    keyword: propKeyword,
    navParam: propNavParam,
    pathWithQueryParams: propPathWithQueryParams,
    variables: propVariables,
    seoGeneratedContentVariables: propSeoGeneratedContentVariables,
    pageWithFAQ,
    isTableView
  } = props;

  const getPreferredValue = (primary, fallback) => (primary || fallback);

  const { customContext } = useContext(ExperienceContext);
  const { search: querystring, pathname } = useLocation();
  const path = `${pathname}${querystring}`;
  const pathWithQueryParams = getPreferredValue(propPathWithQueryParams, path);
  const isSearchPageIndexingEnabled = useConfigService('fs:isSearchPageIndexingEnabled');
  const storeId = useStoreId();
  const location = useLocation();
  let data;
  let error;

  let loading;

  const {
    variables: baseVariables = {},
    variables: { navParam: contextNavParam, keyword: contextKeyword } = {},
  } = customContext || {};

  const keyword = getPreferredValue(propKeyword, contextKeyword);
  const navParam = getPreferredValue(propNavParam, contextNavParam);
  let searchVariables = propVariables || {
    keyword,
    navParam,
    storeId,
  };
  // eslint-disable-next-line
  const isControlledData = typeof props.data !== 'undefined' || typeof props.loading !== 'undefined';
  const skipSearch = isControlledData || (!keyword && !navParam && !propVariables);

  const ctx = getPreferredValue(contextNavParam, contextKeyword);
  if (ctx && !isControlledData) {
    searchVariables = { ...baseVariables, storeId };
  }

  const searchOptions = {
    variables: searchVariables,
    skip: skipSearch,
  };
  ({
    data,
    loading,
    error,
  } = useDataModel('searchModel', searchOptions));
  let nval;
  const seoNavParam = getPreferredValue(propSeoGeneratedContentVariables?.navParam, searchVariables?.navParam);
  if (seoNavParam) {
    nval = (seoNavParam.indexOf('N-') < 0) ? `N-${seoNavParam}` : seoNavParam;
  }

  const decodedSearchTerm = decodeURIComponent(propVariables?.keyword || '').replace(/\s+/g, '-').toLowerCase();
  const { data: emtData } = useDataModel('emtContent', {
    variables: {
      contentId: nval || decodedSearchTerm,
      pageName: 'plp'
    },
    skip: !nval && !decodedSearchTerm && !isSearchPageIndexingEnabled
  });

  if (isControlledData) {
    ({
      data,
      loading,
      error
    } = props);
  }

  const helmetData = {
    ...data,
    emtData,
    location,
    isSearchPageIndexingEnabled
  };
  const helmetProps = {
    ...props,
    pathWithQueryParams
  };
  useHelmet('browseAndSearch',
    { data: helmetData, props: helmetProps },
    browseHandler,
    [keyword, navParam, data?.searchModel]);
  return null;
};

BrowseSearchMetadata.displayName = 'BrowseSearchMetadata';

BrowseSearchMetadata.dataModel = extend({
  searchModel: params({
    keyword: stringType(),
    storefilter: customType('StoreFilter').enum(['ALL', 'IN_STORE', 'ONLINE'], 'ALL'),
    navParam: stringType()
  }).shape({
    metadata: shapeType({
      canonicalUrl: stringType()
    }),
    products: params().arrayOf({
      itemId: stringType(),
      dataSources: stringType(),
      identifiers: shapeType({
        brandName: stringType(),
        itemId: stringType(),
        productLabel: stringType()
      }),
      media: shapeType({
        images: arrayType({
          url: stringType()
        })
      }),
      pricing: params({ storeId: stringType() }).shape({
        value: numberType({ float: true }),
        original: numberType({ float: true })
      }),
      reviews: shapeType({
        ratingsReviews: shapeType({
          averageRating: stringType(),
          totalReviews: stringType()
        })
      })
    }),
    searchReport: shapeType({
      keyword: stringType()
    }),
    taxonomy: shapeType({
      brandLinkUrl: stringType(),
      breadCrumbs: arrayType(shapeType({
        browseUrl: stringType(),
        creativeIconUrl: stringType(),
        deselectUrl: stringType(),
        dimensionId: stringType(),
        dimensionName: stringType(),
        label: stringType(),
        refinementKey: stringType(),
        url: stringType()
      }))
    }),
    templates: arrayType(stringType())
  }),
  emtContent: params({
    contentId: stringType().isRequired(),
    pageName: stringType().isRequired()
  }).shape({
    content: json()
  })
});

const VariablesObject = PropTypes.shape({
  navParam: PropTypes.string
});

BrowseSearchMetadata.propTypes = {
  keyword: PropTypes.string,
  seoGeneratedContentVariables: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.instanceOf(VariablesObject)
  ]),
  navParam: PropTypes.string,
  pathWithQueryParams: PropTypes.string.isRequired,
  data: PropTypes.shape({}),
  loading: PropTypes.bool,
  pageWithFAQ: PropTypes.bool,
  variables: PropTypes.shape({
    keyword: PropTypes.string
  }),
  isTableView: PropTypes.bool
};

BrowseSearchMetadata.defaultProps = {
  keyword: null,
  navParam: null,
  data: undefined,
  pageWithFAQ: undefined,
  loading: undefined,
  seoGeneratedContentVariables: null,
  variables: null,
  isTableView: false
};

const ErrorBoundBrowseAndSearchMetadata = withErrorBoundary(BrowseSearchMetadata);

export { ErrorBoundBrowseAndSearchMetadata as BrowseSearchMetadata };
